import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/contexts/AuthContext'
import {
  agendaService,
  type RendezVous,
  type Creneau,
  type CreneauFormData,
  type RendezVousFormData,
  type AgendaFilters,
  type PastRendezVousFilters,
  type AgendaTimeSlotFormData,
} from '@/services/agenda'

// ==================== CRÉNEAUX RÉCURRENTS (agenda_time_slots) ====================

export function useTimeSlots(adminId?: string) {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['time-slots', adminId],
    queryFn: () => agendaService.getTimeSlots(adminId!),
    enabled: !!user && !!adminId,
  })
}

export function useCreateTimeSlot() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: AgendaTimeSlotFormData) => agendaService.createTimeSlot(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['time-slots'] })
    },
  })
}

export function useUpdateTimeSlot() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<AgendaTimeSlotFormData> }) =>
      agendaService.updateTimeSlot(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['time-slots'] })
    },
  })
}

export function useDeleteTimeSlot() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => agendaService.deleteTimeSlot(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['time-slots'] })
    },
  })
}

// ==================== CRÉNEAUX LEGACY ====================

export function useCreneaux(filters?: {
  professionnelId?: string
  dateFrom?: string
  dateTo?: string
  statut?: string
}) {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['creneaux', filters],
    queryFn: () => agendaService.getCreneaux(filters),
    enabled: !!user,
  })
}

export function useCreateCreneau() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreneauFormData) => agendaService.createCreneau(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['creneaux'] })
    },
  })
}

export function useUpdateCreneau() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<CreneauFormData> }) =>
      agendaService.updateCreneau(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['creneaux'] })
    },
  })
}

export function useDeleteCreneau() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => agendaService.deleteCreneau(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['creneaux'] })
    },
  })
}

// ==================== RENDEZ-VOUS ====================

export function useRendezVous(filters?: AgendaFilters) {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['rendez-vous', filters],
    queryFn: () => agendaService.getRendezVous(filters),
    enabled: !!user,
  })
}

export function useRendezVousById(id?: string) {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['rendez-vous', id],
    queryFn: () => agendaService.getRendezVousById(id!),
    enabled: !!user && !!id,
  })
}

export function useCreateRendezVous() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: RendezVousFormData) => agendaService.createRendezVous(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rendez-vous'] })
      queryClient.invalidateQueries({ queryKey: ['creneaux'] })
      queryClient.invalidateQueries({ queryKey: ['agenda-stats'] })
      queryClient.invalidateQueries({ queryKey: ['rendez-vous', 'pending-count'] })
    },
  })
}

export function useUpdateRendezVous() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<RendezVousFormData> }) =>
      agendaService.updateRendezVous(id, updates),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['rendez-vous'] })
      queryClient.invalidateQueries({ queryKey: ['rendez-vous', data.id] })
      queryClient.invalidateQueries({ queryKey: ['agenda-stats'] })
    },
  })
}

export function useConfirmerRendezVous() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => agendaService.confirmerRendezVous(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['rendez-vous'] })
      queryClient.invalidateQueries({ queryKey: ['rendez-vous', data.id] })
      queryClient.invalidateQueries({ queryKey: ['agenda-stats'] })
      queryClient.invalidateQueries({ queryKey: ['rendez-vous', 'pending-count'] })
    },
  })
}

export function useAnnulerRendezVous() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => agendaService.annulerRendezVous(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['rendez-vous'] })
      queryClient.invalidateQueries({ queryKey: ['rendez-vous', data.id] })
      queryClient.invalidateQueries({ queryKey: ['creneaux'] })
      queryClient.invalidateQueries({ queryKey: ['agenda-stats'] })
      queryClient.invalidateQueries({ queryKey: ['rendez-vous', 'pending-count'] })
    },
  })
}

export function useDeleteRendezVous() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => agendaService.deleteRendezVous(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rendez-vous'] })
      queryClient.invalidateQueries({ queryKey: ['creneaux'] })
      queryClient.invalidateQueries({ queryKey: ['agenda-stats'] })
      queryClient.invalidateQueries({ queryKey: ['rendez-vous', 'pending-count'] })
    },
  })
}

// ==================== STATISTIQUES ====================

export function useAgendaStats(professionnelId?: string) {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['agenda-stats', professionnelId],
    queryFn: () => agendaService.getStats(professionnelId),
    enabled: !!user,
  })
}

export function useTodayRendezVous(professionnelId?: string) {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['rendez-vous', 'today', professionnelId],
    queryFn: () => agendaService.getTodayRendezVous(professionnelId),
    enabled: !!user,
    refetchInterval: 60000, // Refresh every minute
  })
}

export function useRendezVousEnAttente(professionnelId?: string) {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['rendez-vous', 'en-attente', professionnelId],
    queryFn: () => agendaService.getRendezVousEnAttente(professionnelId),
    enabled: !!user,
    refetchInterval: 30000, // Refresh every 30 seconds
  })
}

// ==================== COMPTEUR EN ATTENTE (SIDEBAR) ====================

export function usePendingRdvCount() {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['rendez-vous', 'pending-count'],
    queryFn: async () => {
      const { count, error } = await import('@/lib/supabase').then(m => m.supabase)
        .from('rendez_vous')
        .select('*', { count: 'exact', head: true })
        .eq('statut', 'en_attente')

      if (error) throw error
      return count || 0
    },
    enabled: !!user,
    refetchInterval: 30000,
  })
}

// ==================== RDV PASSÉS ====================

export function usePastRendezVous(filters?: PastRendezVousFilters) {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['rendez-vous', 'past', filters],
    queryFn: () => agendaService.getPastRendezVous(filters),
    enabled: !!user,
  })
}

// ==================== CLIENT BOOKING (PUBLIC) ====================

/**
 * Hook pour récupérer la configuration d'agenda d'un professionnel
 * Utilisé sur la page publique de réservation
 */
export function useAgendaConfig(professionnelId: string) {
  return useQuery({
    queryKey: ['agenda-config', professionnelId],
    queryFn: async () => {
      // TODO: Implémenter la récupération de la config depuis une table agenda_config
      // Pour l'instant, retourner une config par défaut
      return {
        id: professionnelId,
        admin_id: professionnelId,
        default_duration: '60' as const,
        min_booking_notice_hours: 24,
        max_booking_advance_days: 30,
        buffer_before_minutes: 0,
        buffer_after_minutes: 15,
        timezone: 'Europe/Paris',
        is_booking_enabled: true,
        booking_page_url: null,
        notification_email: null,
        send_confirmation_email: true,
        send_reminder_email: true,
        welcome_message: 'Bienvenue sur notre page de réservation',
        confirmation_message: null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }
    },
    enabled: !!professionnelId,
  })
}

/**
 * Hook pour calculer les créneaux disponibles
 * Utilisé sur la page publique de réservation
 * Appelle la RPC public_get_available_slots (SECURITY DEFINER, accessible en anon)
 */
export function useAvailability(
  professionnelId: string,
  startDate: Date,
  endDate: Date,
  durationMinutes: number
) {
  return useQuery({
    queryKey: ['availability', professionnelId, startDate.toISOString(), endDate.toISOString(), durationMinutes],
    queryFn: async () => {
      const { data, error } = await import('@/lib/supabase').then(m => m.supabase)
        .rpc('public_get_available_slots', {
          p_professionnel_id: professionnelId,
          p_start_date: startDate.toISOString(),
          p_end_date: endDate.toISOString(),
          p_duration_minutes: durationMinutes,
        })

      if (error) {
        console.error('Erreur récupération créneaux:', error)
        return []
      }

      // Mapper vers le type AvailableSlot attendu par le TimeSlotPicker
      return (data || []).map((slot: { slot_start: string; slot_end: string; creneau_id: string }) => ({
        start: new Date(slot.slot_start),
        end: new Date(slot.slot_end),
        duration: durationMinutes,
      }))
    },
    enabled: !!professionnelId && !!startDate && !!endDate,
    refetchInterval: 60000, // Rafraîchir chaque minute pour éviter les conflits
  })
}

/**
 * Hook pour créer un RDV depuis la page publique
 * Appelle la RPC public_book_appointment (SECURITY DEFINER, accessible en anon)
 */
export function useBookAppointment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: {
      adminId: string
      slotStart: string
      duration: number
      clientName: string
      clientEmail: string
      clientPhone?: string
      subject: string
      description?: string
    }) => {
      const { data: result, error } = await import('@/lib/supabase').then(m => m.supabase)
        .rpc('public_book_appointment', {
          p_admin_id: data.adminId,
          p_slot_start: data.slotStart,
          p_duration_minutes: data.duration,
          p_client_name: data.clientName,
          p_client_email: data.clientEmail,
          p_client_phone: data.clientPhone || null,
          p_subject: data.subject,
          p_description: data.description || null,
        })

      if (error) {
        throw new Error(`Erreur lors de la réservation: ${error.message}`)
      }

      if (!result?.success) {
        return {
          success: false,
          error: result?.error || 'Une erreur est survenue lors de la réservation.',
        }
      }

      return {
        success: true,
        rdvId: result.rdv_id,
        confirmationToken: result.confirmation_token,
        modificationToken: result.confirmation_token,
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rendez-vous'] })
      queryClient.invalidateQueries({ queryKey: ['availability'] })
      queryClient.invalidateQueries({ queryKey: ['agenda-stats'] })
      queryClient.invalidateQueries({ queryKey: ['rendez-vous', 'pending-count'] })
    },
  })
}
